import { Form, Select, type SelectProps, Spin } from "antd";
import React, { type ReactNode, useMemo, useRef, useState } from "react";
import {
    Controller,
    type FieldPath,
    type FieldValues,
    type RegisterOptions,
    useFormContext,
} from "react-hook-form";

type Option = {
    value: string | number;
    label: ReactNode;
    disabled?: boolean;
};

type RHFSelectProps<TFieldValues extends FieldValues> = {
    name: FieldPath<TFieldValues>;
    rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>;

    fullWidth?: boolean;
    disabled?: boolean;
    loading?: boolean;

    options: ReadonlyArray<Option>;

    onAfterChange?: (value?: string | number) => void;
    onAfterChangeAsync?: (value?: string | number) => Promise<unknown>;
    debounceMs?: number;

    helperText?: ReactNode;
    label?: ReactNode;
    extra?: ReactNode;
    required?: boolean;
    tooltip?: ReactNode;

    inputEndAdornment?: ReactNode;

    selectProps?: Omit<
        SelectProps,
        "options" | "value" | "onChange" | "disabled" | "loading" | "status"
    >;
};

export const RHFSelect = <TFieldValues extends FieldValues>({
    name,
    rules,
    fullWidth = true,
    disabled = false,
    loading,
    options,
    onAfterChange,
    onAfterChangeAsync,
    debounceMs = 0,
    helperText,

    label,
    extra,
    required,
    tooltip,

    inputEndAdornment,
    selectProps,
}: RHFSelectProps<TFieldValues>) => {
    const { control, formState } = useFormContext<TFieldValues>();
    const allDisabled = disabled || formState.isSubmitting;

    const [pending, setPending] = useState(false);
    const timerRef = useRef<number | null>(null);

    const handleAfterChange = useMemo(() => {
        return (value?: string | number) => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            const run = async () => {
                try {
                    onAfterChange?.(value);
                    if (onAfterChangeAsync) {
                        setPending(true);
                        await onAfterChangeAsync(value);
                    }
                } finally {
                    if (onAfterChangeAsync) setPending(false);
                }
            };

            if (debounceMs > 0) {
                timerRef.current = window.setTimeout(run, debounceMs);
            } else {
                void run();
            }
        };
    }, [onAfterChange, onAfterChangeAsync, debounceMs]);

    const isLoading = Boolean(loading) || pending;

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => {
                const validateStatus = fieldState.error ? "error" : undefined;
                const help = fieldState.error?.message ?? helperText;

                return (
                    <Form.Item
                        label={label}
                        extra={extra}
                        required={required}
                        tooltip={tooltip}
                        validateStatus={validateStatus}
                        help={help}
                        style={{
                            ...(fullWidth && { width: "100%" }),
                            margin: 0,
                        }}
                    >
                        <Select
                            {...selectProps}
                            style={{
                                width: fullWidth ? "100%" : undefined,
                                ...(selectProps?.style ?? {}),
                            }}
                            disabled={allDisabled}
                            loading={isLoading}
                            value={field.value ?? undefined}
                            options={options.map((o) => ({
                                value: o.value,
                                label: o.label,
                                disabled: o.disabled,
                            }))}
                            // аналог endAdornment: показати спінер справа + твій adornment
                            suffixIcon={
                                <>
                                    {isLoading ? <Spin size="small" /> : null}
                                    {inputEndAdornment}
                                </>
                            }
                            onChange={(value) => {
                                field.onChange(value);
                                handleAfterChange(value);
                            }}
                            status={validateStatus}
                        />
                    </Form.Item>
                );
            }}
        />
    );
};
